import { atom, computed, transact, type Atom } from "@tldraw/state";
import {
  CameraRecordType,
  InstancePageStateRecordType,
  PageRecordType,
  StyleProp,
  TLDOCUMENT_ID,
  TLINSTANCE_ID,
  TLPOINTER_ID,
  createBindingId,
  createShapeId,
  isShapeId,
  type TLAsset,
  type TLAssetPartial,
  type TLBinding,
  type TLBindingCreate,
  type TLCamera,
  type TLCursor,
  type TLInstance,
  type TLInstancePageState,
  type TLInstancePresence,
  type TLPage,
  type TLPageId,
  type TLParentId,
  type TLRecord,
  type TLShape,
  type TLShapeId,
  type TLShapePartial,
  type TLStore,
  type TLTheme,
  type TLThemes,
} from "@tldraw/tlschema";
import {
  getIndexAbove,
  getIndexBelow,
  getIndicesBetween,
  type IndexKey,
} from "@tldraw/utils";
import type { Editor as RichTextEditor } from "@tiptap/core";
import {
  Box,
  Vec,
  type BoxLike,
  type BoxModel,
  type VecLike,
} from "../geometry";
import type {
  CameraMoveOptions,
  CameraOptions,
  CanvasContent,
  CanvasEventInfo,
  CanvasInputs,
  CanvasTextMeasure,
  CanvasUserPreferences,
  CreateShapePartial,
  HistoryOptions,
  ImageExportOptions,
  PlanroomEditor,
  RunOptions,
  ShapeAtPointOptions,
} from "../editor";
import { HistoryManager } from "./history";
import { Mat } from "./math";
import type { Geometry2d } from "./geometry2d";
import type {
  ResizeHandle,
  ResizeInfo,
  ShapeUtilConstructor,
  ShapeUtilLike,
} from "./shape-util";
import {
  StateNode,
  type EventInfo,
  type StateNodeConstructor,
} from "./state-node";
import { planroomTheme } from "./theme";
import { exportSvg, rasterize, registerFonts, type CanvasFont } from "./export";

/** Planroom content on the clipboard travels as tagged JSON text. */
export const CLIPBOARD_TAG = "planroom-content:";

export type CanvasEditorOptions = {
  store: TLStore;
  shapeUtils: ShapeUtilConstructor[];
  tools: StateNodeConstructor[];
  initialTool?: string;
  getShapeVisibility?(
    shape: TLShape,
    editor: PlanroomEditor,
  ): "visible" | "hidden" | "inherit" | null | undefined;
  /** Whether a shape class owns children the way sheets do. */
  isFrameLike?(util: ShapeUtilLike): boolean;
  cameraOptions?: Partial<CameraOptions>;
  /** Used for shape types without a class here, so boards still open. */
  fallbackUtil?: ShapeUtilConstructor;
  /** Keyboard shortcuts such as "mod+d", replacing the engine's defaults. */
  shortcuts?: Record<string, (editor: CanvasEditor) => void>;
  /** Called when a tool that does not exist in this engine is chosen. */
  onUnknownTool?(id: string): void;
  /** Font files for the theme's families: loaded on screen, embedded in exports. */
  fonts?: CanvasFont[];
};

/** Content from outside the canvas: dropped or pasted files, text and links. */
export type ExternalContent =
  | { type: "files"; files: File[]; point?: VecLike }
  | { type: "text"; text: string; point?: VecLike }
  | { type: "url"; url: string; point?: VecLike }
  | { type: "content"; content: CanvasContent; point?: VecLike };

type Shapes = TLShape[] | TLShapeId[];
const ids = (shapes: Shapes) =>
  shapes.map((s) => (typeof s === "string" ? s : s.id)) as TLShapeId[];
const idOf = (s: TLShape | TLShapeId) => (typeof s === "string" ? s : s.id);
const ZOOM_STEPS = [0.05, 0.1, 0.25, 0.5, 1, 2, 4, 8];

class Emitter {
  private readonly handlers = new Map<string, Set<(info: any) => void>>();
  on(event: string, fn: (info: any) => void) {
    (
      this.handlers.get(event) ??
      this.handlers.set(event, new Set()).get(event)!
    ).add(fn);
  }
  off(event: string, fn: (info: any) => void) {
    this.handlers.get(event)?.delete(fn);
  }
  emit(event: string, info: unknown) {
    for (const fn of this.handlers.get(event) ?? []) fn(info);
  }
}

/** A guide through the points that share a snapped x (vertical) or y (horizontal). */
export type SnapLine = { id: string; points: VecLike[] };

/** Planroom's editor: the PlanroomEditor contract over an MIT record store. */
export class CanvasEditor implements PlanroomEditor {
  readonly store: TLStore;
  readonly sideEffects: TLStore["sideEffects"];
  readonly history: HistoryManager;
  readonly root: StateNode;
  readonly utils = new Map<string, ShapeUtilLike>();
  private readonly emitter = new Emitter();
  private readonly opts: CanvasEditorOptions;
  private container: HTMLElement | null = null;
  private readonly screenBounds = atom("screen bounds", new Box(0, 0, 1, 1));
  private readonly cameraOptions: Atom<CameraOptions>;
  private readonly richText = atom<RichTextEditor | null>(
    "rich text editor",
    null,
  );
  private readonly toolPath = atom("tool path", "");
  private readonly snapMode = atom("snap mode", false);
  private readonly snapLines = atom<SnapLine[]>("snap lines", []);
  private animation = 0;
  private readonly geometryCache;
  private readonly transformCache;
  private readonly disposers: Array<() => void> = [];

  readonly inputs: CanvasInputs & {
    shiftKey: boolean;
    altKey: boolean;
    ctrlKey: boolean;
    metaKey: boolean;
    isPanning: boolean;
    isPointerDown: boolean;
    isDragging: boolean;
    currentScreenPoint: Vec;
    currentPagePoint: Vec;
    originScreenPoint: Vec;
    originPagePoint: Vec;
  };
  readonly options = { dragDistanceSquared: 16, coarseDragDistanceSquared: 36 };
  readonly overlays = { getOverlayAtPoint: () => undefined };
  readonly user: CanvasUserPreferences;
  readonly textMeasure: CanvasTextMeasure;

  constructor(opts: CanvasEditorOptions) {
    this.opts = opts;
    registerFonts(opts.fonts);
    this.store = opts.store;
    this.sideEffects = opts.store.sideEffects;
    this.cameraOptions = atom("camera options", {
      isLocked: false,
      panSpeed: 1,
      zoomSpeed: 1,
      zoomSteps: ZOOM_STEPS,
      wheelBehavior: "pan",
      ...opts.cameraOptions,
    });
    for (const Util of opts.shapeUtils)
      this.utils.set(Util.type, new Util(this));
    this.fallback = opts.fallbackUtil ? new opts.fallbackUtil(this) : undefined;
    const editor = this;
    this.inputs = {
      shiftKey: false,
      altKey: false,
      ctrlKey: false,
      metaKey: false,
      isPanning: false,
      isPointerDown: false,
      isDragging: false,
      currentScreenPoint: new Vec(),
      currentPagePoint: new Vec(),
      originScreenPoint: new Vec(),
      originPagePoint: new Vec(),
      getCurrentPagePoint() {
        return editor.inputs.currentPagePoint.clone();
      },
      getOriginPagePoint() {
        return editor.inputs.originPagePoint.clone();
      },
      getIsDragging() {
        return editor.inputs.isDragging;
      },
    };
    this.user = {
      getIsSnapMode: () => this.snapMode.get(),
      updateUserPreferences: (p) => {
        if (p.isSnapMode !== undefined) this.snapMode.set(p.isSnapMode);
      },
    };
    this.textMeasure = { measureHtml: measureHtml };
    this.geometryCache = this.store.createComputedCache<Geometry2d, TLShape>(
      "geometry",
      (shape) => this.getUtil(shape).getGeometry(shape) as Geometry2d,
    );
    this.transformCache = this.store.createComputedCache<Mat, TLShape>(
      "page transform",
      (shape) => {
        const local = Mat.Compose(shape.x, shape.y, shape.rotation);
        const parent = isShapeId(shape.parentId)
          ? this.getShape(shape.parentId)
          : undefined;
        return parent
          ? this.getShapePageTransform(parent).multiply(local)
          : local;
      },
    );
    this.history = new HistoryManager(
      this.store,
      () => this.getSelectedShapeIds(),
      (ids) => this.setSelectedShapes(ids.filter((id) => this.getShape(id))),
      (before, after) => {
        if (this.getIsReadonly()) return false;
        const record = before ?? after;
        if (record?.typeName !== "shape") return true;
        const ownLockChange = before?.typeName === "shape" && after?.typeName === "shape" && before.isLocked !== after.isLocked;
        if (!ownLockChange && record.isLocked) return false;
        const parent = this.getShape(record.parentId);
        return !parent || !this.isShapeOrAncestorLocked(parent);
      },
    );
    this.installShapeHooks();
    this.ensureSessionRecords();
    const Root = class extends StateNode {
      static override id = "root";
      static override initial = opts.initialTool ?? "select";
      static override children = () => opts.tools;
    };
    this.root = new Root(this);
    this.root.enter({}, "");
    this.syncToolPath();
  }

  dispose() {
    for (const dispose of this.disposers) dispose();
    this.history.dispose();
    this.stopCameraAnimation();
  }

  // ----- shapes and their classes -----

  private readonly fallback?: ShapeUtilLike;
  getUtil(shape: TLShape | string): ShapeUtilLike {
    const type = typeof shape === "string" ? shape : shape.type;
    const util = this.utils.get(type) ?? this.fallback;
    if (!util) throw new Error(`No shape class for "${type}"`);
    return util;
  }
  getShapeUtil(shape: TLShape | string) {
    return this.getUtil(shape);
  }
  hasShapeUtil(type: string) {
    return this.utils.has(type);
  }

  private installShapeHooks() {
    const se = this.store.sideEffects;
    this.disposers.push(
      se.registerBeforeCreateHandler("shape", (next, source) => {
        if (source !== "user") return next;
        return (
          (this.utils.get(next.type)?.onBeforeCreate?.(next) as TLShape) ?? next
        );
      }),
      se.registerBeforeChangeHandler("shape", (prev, next, source) => {
        if (source !== "user") return next;
        return (
          (this.utils
            .get(next.type)
            ?.onBeforeUpdate?.(prev, next) as TLShape) ?? next
        );
      }),
      // Shapes bound to a deleted shape (arrows) keep their drawn position.
      se.registerBeforeDeleteHandler("shape", (shape, source) => {
        if (source !== "user") return;
        for (const r of this.store.query.records("binding").get()) {
          const binding = r as TLBinding;
          if (binding.toId !== shape.id) continue;
          const from = this.getShape(binding.fromId);
          const util = from && this.utils.get(from.type);
          (
            util as
              | { onBindingTargetDelete?(s: TLShape, b: TLBinding): void }
              | undefined
          )?.onBindingTargetDelete?.(from!, binding);
        }
      }),
      // Deleting a shape deletes its children and bindings to it.
      se.registerAfterDeleteHandler("shape", (shape, source) => {
        if (source !== "user") return;
        const children = this.getSortedChildIdsForParent(shape.id);
        if (children.length) this.store.remove(children);
        const bindings = this.store
          .allRecords()
          .filter(
            (r): r is TLBinding =>
              r.typeName === "binding" &&
              ((r as TLBinding).fromId === shape.id ||
                (r as TLBinding).toId === shape.id),
          )
          .map((b) => b.id);
        if (bindings.length) this.store.remove(bindings);
      }),
    );
  }

  private ensureSessionRecords() {
    const types = this.store.schema.types as unknown as Record<
      string,
      { create(r: object): TLRecord }
    >;
    this.history.run(() => {
      transact(() => {
        let pageId = this.getPages()[0]?.id;
        if (!pageId) {
          pageId = PageRecordType.createId("page");
          this.store.put([
            PageRecordType.create({
              id: pageId,
              name: "Page 1",
              index: "a1" as IndexKey,
            }),
          ]);
        }
        const instance = this.store.get(TLINSTANCE_ID);
        if (!instance || !this.store.get(instance.currentPageId))
          this.store.put([
            types.instance.create({
              ...instance,
              id: TLINSTANCE_ID,
              currentPageId: pageId,
            }),
          ]);
        if (!this.store.get(TLPOINTER_ID))
          this.store.put([types.pointer.create({ id: TLPOINTER_ID })]);
        this.ensurePageState(this.getCurrentPageId());
      });
    }, "ignore");
  }

  private ensurePageState(pageId: TLPageId) {
    const types = this.store.schema.types as unknown as Record<
      string,
      { create(r: object): TLRecord }
    >;
    const stateId = InstancePageStateRecordType.createId(pageId);
    const cameraId = CameraRecordType.createId(pageId);
    const missing: TLRecord[] = [];
    if (!this.store.get(stateId))
      missing.push(types.instance_page_state.create({ id: stateId, pageId }));
    if (!this.store.get(cameraId))
      missing.push(types.camera.create({ id: cameraId }));
    if (missing.length)
      this.history.run(() => this.store.put(missing), "ignore");
  }

  // ----- lifecycle, focus, events -----

  setContainer(element: HTMLElement | null) {
    this.container = element;
    if (element) this.updateViewportScreenBounds(element);
  }
  getContainer() {
    if (!this.container) throw new Error("The canvas is not mounted");
    return this.container;
  }
  focus(_opts?: { focusContainer?: boolean }) {
    this.container?.focus({ preventScroll: true });
    this.updateInstanceState({ isFocused: true }, { history: "ignore" });
    return this;
  }
  blur(_opts?: { blurContainer?: boolean }) {
    this.container?.blur();
    this.updateInstanceState({ isFocused: false }, { history: "ignore" });
    return this;
  }
  getIsReadonly() {
    const mode = (
      this.store.props as {
        collaboration?: { mode?: { get(): string } | null };
      }
    ).collaboration?.mode;
    return mode?.get() === "readonly" || this.getInstanceState().isReadonly;
  }
  getColorMode(): "dark" | "light" {
    return "light";
  }
  private readonly theme = atom<TLTheme>("theme", planroomTheme);
  getCurrentTheme(): TLTheme {
    return this.theme.get();
  }
  /** Adjust the theme's colours (only the default theme is used). */
  updateThemes(themes: ((themes: TLThemes) => TLThemes) | TLThemes) {
    const copy = JSON.parse(JSON.stringify(this.theme.get())) as TLTheme;
    const next = (typeof themes === "function" ? themes({ default: copy } as TLThemes) : themes) as unknown as Record<
      string,
      TLTheme
    >;
    this.theme.set(next.default ?? Object.values(next)[0] ?? copy);
    return this;
  }
  on(
    event: "event" | "before-event",
    handler: (info: CanvasEventInfo) => void,
  ) {
    this.emitter.on(event, handler);
    return this;
  }
  off(
    event: "event" | "before-event",
    handler: (info: CanvasEventInfo) => void,
  ) {
    this.emitter.off(event, handler);
    return this;
  }

  /** Route an input event to the active tool. */
  dispatch(info: EventInfo) {
    this.emitter.emit("before-event", info);
    if (
      info.type === "pointer" ||
      info.type === "click" ||
      info.type === "keyboard" ||
      info.type === "wheel"
    ) {
      this.inputs.shiftKey = info.shiftKey;
      this.inputs.altKey = info.altKey;
      this.inputs.ctrlKey = info.ctrlKey;
      this.inputs.metaKey = info.metaKey;
    }
    if (info.type === "pointer") this.trackPointer(info);
    this.root.handleEvent(info);
    this.syncToolPath();
    if ((info.name === "pointer_up" && this.isInAny("select.idle", "select.crop.idle")) ||
        (info.name === "key_up" && info.key.startsWith("Arrow"))) this.history.commit();
    this.emitter.emit("event", info);
  }

  private trackPointer(info: Extract<EventInfo, { type: "pointer" }>) {
    const inputs = this.inputs;
    inputs.currentScreenPoint = info.point.clone();
    inputs.currentPagePoint = info.pagePoint.clone();
    if (info.name === "pointer_down") {
      inputs.isPointerDown = true;
      inputs.isDragging = false;
      inputs.originScreenPoint = info.point.clone();
      inputs.originPagePoint = info.pagePoint.clone();
    } else if (
      info.name === "pointer_move" &&
      inputs.isPointerDown &&
      !inputs.isDragging
    ) {
      const d = Vec.Sub(info.point, inputs.originScreenPoint);
      if (d.x * d.x + d.y * d.y > this.options.dragDistanceSquared)
        inputs.isDragging = true;
    } else if (info.name === "pointer_up") {
      inputs.isPointerDown = false;
    }
    this.history.run(
      () =>
        this.store.update(TLPOINTER_ID, (p) => ({
          ...p,
          x: info.pagePoint.x,
          y: info.pagePoint.y,
          lastActivityTimestamp: Date.now(),
        })),
      "ignore",
    );
  }

  private syncToolPath() {
    const path = this.root.getCurrent()?.getPath() ?? "";
    if (this.toolPath.__unsafe__getWithoutCapture() !== path)
      this.toolPath.set(path);
  }

  getInstanceState(): TLInstance {
    return this.store.get(TLINSTANCE_ID)!;
  }
  updateInstanceState(
    partial: Partial<Omit<TLInstance, "currentPageId">>,
    historyOptions?: HistoryOptions,
  ) {
    this.history.run(
      () => this.store.update(TLINSTANCE_ID, (i) => ({ ...i, ...partial })),
      historyOptions?.history ?? "ignore",
    );
    return this;
  }
  getDocumentSettings() {
    return this.store.get(TLDOCUMENT_ID)!;
  }
  getCollaborators(): TLInstancePresence[] {
    return this.store.query
      .records("instance_presence")
      .get()
      .filter((p) => p.currentPageId === this.getCurrentPageId());
  }
  getResizeScaleFactor() {
    return 1;
  }
  getHitTestMargin() {
    return 8 / this.getZoomLevel();
  }

  // ----- history -----

  run(fn: () => void, opts?: RunOptions) {
    this.history.run(() => transact(fn), opts?.history ?? "record");
    return this;
  }
  markHistoryStoppingPoint(name?: string, options?: { hold?: boolean; checkpoint?: boolean }) {
    return this.history.mark(name, options);
  }
  commitHistoryStoppingPoint(id?: string) { this.history.commit(id); return this; }
  beginHistoryInput(label: string) { return this.history.beginInput(label); }
  getHistoryState() {
    const state = this.history.getState();
    const rich = this.getRichTextEditor();
    if (rich && !rich.isDestroyed)
      return { ...state, undo: rich.can().undo() ? "typing" : null, redo: rich.can().redo() ? "typing" : null };
    return state;
  }
  bailToMark(id: string) {
    this.history.bailToMark(id);
    return this;
  }
  undo() {
    if (this.getIsReadonly()) return this;
    const rich = this.getRichTextEditor();
    if (rich && !rich.isDestroyed) { rich.commands.undo(); this.history.touch(); return this; }
    if (this.cancelInteractionForHistory()) return this;
    this.history.undo();
    return this;
  }
  redo() {
    if (this.getIsReadonly()) return this;
    const rich = this.getRichTextEditor();
    if (rich && !rich.isDestroyed) { rich.commands.redo(); this.history.touch(); return this; }
    if (this.cancelInteractionForHistory()) return this;
    this.history.redo();
    return this;
  }
  private cancelInteractionForHistory() {
    if (this.inputs.isPointerDown || this.history.isInProgress()) {
      this.cancel();
      this.inputs.isPointerDown = false;
      this.inputs.isDragging = false;
      this.setCurrentTool("select");
      return true;
    }
    return false;
  }
  clearHistory() {
    this.history.clear();
    return this;
  }
  isReplayingHistory() {
    return this.history.isReplaying();
  }

  // ----- pages -----

  getCurrentPageId(): TLPageId {
    return this.getInstanceState().currentPageId;
  }
  getPages(): TLPage[] {
    return this.store.query
      .records("page")
      .get()
      .slice()
      .sort((a, b) => (a.index < b.index ? -1 : a.index > b.index ? 1 : 0));
  }
  getPage(page: TLPage | TLPageId) {
    return this.store.get(typeof page === "string" ? page : page.id) as
      TLPage | undefined;
  }
  setCurrentPage(page: TLPage | TLPageId) {
    const id = typeof page === "string" ? page : page.id;
    this.ensurePageState(id);
    this.history.run(
      () =>
        this.store.update(TLINSTANCE_ID, (i) => ({ ...i, currentPageId: id })),
      "ignore",
    );
    return this;
  }
  private pageState(): TLInstancePageState {
    const id = InstancePageStateRecordType.createId(this.getCurrentPageId());
    return (
      (this.store.get(id) as TLInstancePageState | undefined) ??
      ({
        selectedShapeIds: [],
        hintingShapeIds: [],
        erasingShapeIds: [],
        editingShapeId: null,
        croppingShapeId: null,
        hoveredShapeId: null,
        focusedGroupId: null,
      } as unknown as TLInstancePageState)
    );
  }
  private updatePageState(partial: Partial<TLInstancePageState>) {
    const id = InstancePageStateRecordType.createId(this.getCurrentPageId());
    this.ensurePageState(this.getCurrentPageId());
    this.history.run(
      () => this.store.update(id, (s) => ({ ...s, ...partial })),
      "ignore",
    );
  }

  private readonly childIndex = computed("children by parent", () => {
    const index = this.store.query.index("shape", "parentId").get();
    return index;
  });

  getSortedChildIdsForParent(
    parent: TLPage | TLParentId | TLShape,
  ): TLShapeId[] {
    const id = typeof parent === "string" ? parent : parent.id;
    const children = this.childIndex.get().get(id as never);
    if (!children?.size) return [];
    return [...children]
      .map((cid) => this.store.get(cid as TLShapeId) as TLShape)
      .filter(Boolean)
      .sort((a, b) => (a.index < b.index ? -1 : a.index > b.index ? 1 : 0))
      .map((s) => s.id);
  }

  /** Every shape on the current page in paint order (parents before children). */
  getCurrentPageShapesSorted(): TLShape[] {
    const out: TLShape[] = [];
    const walk = (parent: TLParentId) => {
      for (const id of this.getSortedChildIdsForParent(parent)) {
        const shape = this.getShape(id);
        if (!shape) continue;
        out.push(shape);
        walk(id);
      }
    };
    walk(this.getCurrentPageId());
    return out;
  }
  getCurrentPageShapes() {
    return this.getCurrentPageShapesSorted();
  }
  getCurrentPageShapeIds() {
    return new Set(this.getCurrentPageShapesSorted().map((s) => s.id));
  }
  getPageShapeIds(page: TLPage | TLPageId) {
    const out = new Set<TLShapeId>();
    const walk = (parent: TLParentId) => {
      for (const id of this.getSortedChildIdsForParent(parent)) {
        out.add(id);
        walk(id);
      }
    };
    walk(typeof page === "string" ? page : page.id);
    return out;
  }
  getCurrentPageBounds() {
    const boxes = this.getSortedChildIdsForParent(this.getCurrentPageId())
      .map((id) => this.getShapePageBounds(id))
      .filter((b): b is Box => !!b);
    return boxes.length ? Box.Common(boxes) : undefined;
  }

  // ----- shape records -----

  getShape<T extends TLShape = TLShape>(
    shape: TLParentId | TLShape,
  ): T | undefined {
    const id = typeof shape === "string" ? shape : shape.id;
    if (!isShapeId(id)) return undefined;
    return this.store.get(id) as T | undefined;
  }

  private complete(partial: CreateShapePartial): TLShape {
    const util = this.getUtil(partial.type);
    const parentId = (partial.parentId ??
      this.getCurrentPageId()) as TLParentId;
    return {
      id: partial.id ?? createShapeId(),
      typeName: "shape",
      type: partial.type,
      x: partial.x ?? 0,
      y: partial.y ?? 0,
      rotation: partial.rotation ?? 0,
      isLocked: partial.isLocked ?? false,
      opacity:
        partial.opacity ?? this.getInstanceState().opacityForNextShape ?? 1,
      parentId,
      index: partial.index ?? this.getHighestIndexForParent(parentId),
      meta: { ...(partial.meta ?? {}) },
      props: {
        ...this.defaultProps(partial.type, util),
        ...(partial.props ?? {}),
      },
    } as TLShape;
  }

  /** Default props, with this editor's styles for the next shape applied. */
  private defaultProps(type: string, util: ShapeUtilLike) {
    const props = { ...(util.getDefaultProps?.() ?? {}) } as Record<
      string,
      unknown
    >;
    const styles = this.getInstanceState().stylesForNextShape as Record<
      string,
      unknown
    >;
    for (const [key, style] of this.styleProps(type))
      if (styles[style.id] !== undefined) props[key] = styles[style.id];
    return props;
  }
  private styleProps(type: string): Array<[string, StyleProp<unknown>]> {
    const Util = this.utils.get(type)?.constructor as {
      props?: Record<string, unknown>;
    };
    return Object.entries(Util?.props ?? {}).filter(
      (e): e is [string, StyleProp<unknown>] => e[1] instanceof StyleProp,
    );
  }

  createShape<T extends TLShape>(shape: CreateShapePartial<T>) {
    return this.createShapes([shape]);
  }
  createShapes<T extends TLShape = TLShape>(shapes: CreateShapePartial<T>[]) {
    if (this.getIsReadonly()) return this;
    const records: TLShape[] = [];
    // Siblings created together get increasing indices.
    const lastIndex = new Map<string, IndexKey>();
    for (const given of shapes as CreateShapePartial[]) {
      // Without a parent, a shape drawn over a sheet (or any frame that takes
      // its type) belongs to that sheet, as it did with tldraw's frames.
      const partial = given.parentId ? given : this.placeInFrameAt(given);
      const parentId = (partial.parentId ??
        this.getCurrentPageId()) as TLParentId;
      let index = partial.index;
      if (!index) {
        const after =
          lastIndex.get(parentId) ?? this.getHighestIndexForParent(parentId);
        index = lastIndex.has(parentId) ? getIndexAbove(after) : after;
      }
      lastIndex.set(parentId, index);
      records.push(this.complete({ ...partial, parentId, index }));
    }
    this.store.put(records);
    return this;
  }

  /** The topmost frame-like shape under a page point that accepts a new shape of this type. */
  getParentIdForNewShapeAt(point: VecLike, type: string): TLParentId {
    const shapes = this.getCurrentPageShapesSorted();
    for (let i = shapes.length - 1; i >= 0; i--) {
      const shape = shapes[i];
      if (!this.isShapeFrameLike(shape) || this.isShapeHidden(shape)) continue;
      if (!this.getUtil(shape).canReceiveNewChildrenOfType?.(shape, type))
        continue;
      const local = this.getPointInShapeSpace(shape, point);
      if (this.getShapeGeometry(shape).bounds.containsPoint(local))
        return shape.id;
    }
    return this.getCurrentPageId();
  }
  /** A page-positioned partial, moved into the frame under its origin when one takes it. */
  private placeInFrameAt(partial: CreateShapePartial): CreateShapePartial {
    const point = { x: partial.x ?? 0, y: partial.y ?? 0 };
    const parentId = this.getParentIdForNewShapeAt(point, partial.type);
    if (!isShapeId(parentId)) return partial;
    const transform = this.getShapePageTransform(parentId);
    const local = transform.invert().applyToPoint(point);
    return {
      ...partial,
      parentId,
      x: local.x,
      y: local.y,
      rotation: (partial.rotation ?? 0) - transform.rotation(),
    };
  }

  updateShape<T extends TLShape = TLShape>(
    partial: null | TLShapePartial<T> | undefined,
  ) {
    return this.updateShapes([partial]);
  }
  updateShapes<T extends TLShape>(
    partials: (null | TLShapePartial<T> | undefined)[],
  ) {
    if (this.getIsReadonly()) return this;
    const updates: TLShape[] = [];
    for (const partial of partials) {
      if (!partial) continue;
      const shape = this.getShape(partial.id);
      if (!shape) continue;
      const onlyLock = Object.keys(partial).every((k) =>
        ["id", "type", "isLocked"].includes(k),
      );
      if (this.isShapeOrAncestorLocked(shape) && !onlyLock && !this.ignoreLock)
        continue;
      updates.push({
        ...shape,
        ...partial,
        props: { ...shape.props, ...((partial as TLShapePartial).props ?? {}) },
        meta: { ...shape.meta, ...((partial as TLShapePartial).meta ?? {}) },
      } as TLShape);
    }
    if (updates.length) this.store.put(updates);
    return this;
  }
  private ignoreLock = false;

  deleteShape(shape: TLShapeId | TLShape) {
    return this.deleteShapes([shape] as Shapes);
  }
  deleteShapes(shapes: Shapes) {
    if (this.getIsReadonly()) return this;
    const all = [...this.getShapeAndDescendantIds(ids(shapes))].filter(
      (id) => !this.isShapeOrAncestorLocked(id) || this.ignoreLock,
    );
    if (all.length) this.store.remove(all);
    const selected = this.getSelectedShapeIds().filter((id) =>
      this.getShape(id),
    );
    if (selected.length !== this.getSelectedShapeIds().length)
      this.setSelectedShapes(selected);
    return this;
  }

  duplicateShapes(shapes: Shapes, offset: VecLike = { x: 16, y: 16 }) {
    const content = this.getContentFromCurrentPage(shapes);
    if (!content) return this;
    const before = new Set(this.getCurrentPageShapeIds());
    this.putContentOntoCurrentPage(content, {
      preservePosition: true,
      select: false,
      duplicateOffset: offset,
    });
    const created = [...this.getCurrentPageShapeIds()].filter(
      (id) => !before.has(id),
    );
    const roots = created.filter((id) => {
      const s = this.getShape(id)!;
      return !created.includes(s.parentId as TLShapeId);
    });
    this.select(...roots);
    return this;
  }

  /** Move shapes to a new parent, keeping their position on the page. */
  reparentShapes(shapes: Shapes, parentId: TLParentId, insertIndex?: IndexKey) {
    const parentTransform = isShapeId(parentId)
      ? this.getShapePageTransform(parentId)
      : Mat.Identity();
    const inverse = parentTransform.invert();
    const parentRotation = parentTransform.rotation();
    let index = insertIndex ?? this.getHighestIndexForParent(parentId);
    const updates: TLShape[] = [];
    for (const id of ids(shapes)) {
      const shape = this.getShape(id);
      if (!shape || shape.parentId === parentId) continue;
      const page = this.getShapePageTransform(shape);
      const local = inverse.applyToPoint(page.point());
      updates.push({
        ...shape,
        parentId,
        index,
        x: local.x,
        y: local.y,
        rotation: page.rotation() - parentRotation,
      });
      index = getIndexAbove(index);
    }
    if (updates.length) this.store.put(updates);
    return this;
  }

  resizeShape(shape: TLShape | TLShapeId, scale: VecLike) {
    const current = this.getShape(idOf(shape));
    if (!current) return this;
    const util = this.getUtil(current);
    const bounds = this.getShapeGeometry(current).bounds;
    const info: ResizeInfo = {
      newPoint: new Vec(current.x, current.y),
      handle: "bottom_right",
      mode: "resize_bounds",
      scaleX: scale.x,
      scaleY: scale.y,
      initialBounds: bounds,
      initialShape: current,
    };
    const partial = util.onResize?.(current, info);
    if (partial)
      this.updateShape({ ...partial, id: current.id, type: current.type });
    return this;
  }

  nudgeShapes(shapes: Shapes, offset: VecLike) {
    const updates: TLShapePartial[] = [];
    for (const id of ids(shapes)) {
      const shape = this.getShape(id);
      if (!shape) continue;
      const parentRotation = isShapeId(shape.parentId)
        ? this.getShapePageTransform(shape.parentId).rotation()
        : 0;
      const local = new Vec(
        offset.x * Math.cos(-parentRotation) -
          offset.y * Math.sin(-parentRotation),
        offset.x * Math.sin(-parentRotation) +
          offset.y * Math.cos(-parentRotation),
      );
      updates.push({
        id,
        type: shape.type,
        x: shape.x + local.x,
        y: shape.y + local.y,
      });
    }
    return this.updateShapes(updates);
  }

  bringToFront(shapes: Shapes) {
    const byParent = this.groupByParent(ids(shapes));
    const updates: TLShapePartial[] = [];
    for (const [parent, members] of byParent) {
      let index = this.getHighestIndexForParent(parent);
      for (const shape of members) {
        updates.push({ id: shape.id, type: shape.type, index });
        index = getIndexAbove(index);
      }
    }
    return this.updateShapes(updates);
  }
  sendToBack(shapes: Shapes) {
    const byParent = this.groupByParent(ids(shapes));
    const updates: TLShapePartial[] = [];
    for (const [parent, members] of byParent) {
      const siblings = this.getSortedChildIdsForParent(parent)
        .map((id) => this.getShape(id)!)
        .filter((s) => !members.includes(s));
      const lowest = siblings[0]?.index;
      const indices = getIndicesBetween(undefined, lowest, members.length);
      members.forEach((shape, i) =>
        updates.push({ id: shape.id, type: shape.type, index: indices[i] }),
      );
    }
    return this.updateShapes(updates);
  }
  /** Move each shape one step up past the next sibling it is not moving with. */
  bringForward(shapes: Shapes) {
    return this.stepOrder(shapes, 1);
  }
  /** Move each shape one step down past the next sibling it is not moving with. */
  sendBackward(shapes: Shapes) {
    return this.stepOrder(shapes, -1);
  }
  private stepOrder(shapes: Shapes, direction: 1 | -1) {
    const updates: TLShapePartial[] = [];
    for (const [parent, members] of this.groupByParent(ids(shapes))) {
      const moving = new Set(members.map((s) => s.id));
      const order = this.getSortedChildIdsForParent(parent);
      const others = order.filter((id) => !moving.has(id));
      // The sibling to pass: next above the top member, or next below the bottom one.
      const edge = direction > 0 ? members[members.length - 1] : members[0];
      const at = order.indexOf(edge.id);
      const passId =
        direction > 0
          ? order.slice(at + 1).find((id) => !moving.has(id))
          : [...order.slice(0, at)].reverse().find((id) => !moving.has(id));
      if (!passId) continue;
      const passAt = others.indexOf(passId);
      const indexOf = (id: TLShapeId | undefined) =>
        id ? this.getShape(id)?.index : undefined;
      const lower = indexOf(direction > 0 ? passId : others[passAt - 1]);
      const upper = indexOf(direction > 0 ? others[passAt + 1] : passId);
      const indices = getIndicesBetween(lower, upper, members.length);
      members.forEach((shape, i) =>
        updates.push({ id: shape.id, type: shape.type, index: indices[i] }),
      );
    }
    return this.updateShapes(updates);
  }
  private groupByParent(shapeIds: TLShapeId[]) {
    const out = new Map<TLParentId, TLShape[]>();
    for (const id of shapeIds) {
      const shape = this.getShape(id);
      if (!shape) continue;
      (
        out.get(shape.parentId) ??
        out.set(shape.parentId, []).get(shape.parentId)!
      ).push(shape);
    }
    for (const members of out.values())
      members.sort((a, b) => (a.index < b.index ? -1 : 1));
    return out;
  }

  toggleLock(shapes: Shapes) {
    const list = ids(shapes)
      .map((id) => this.getShape(id))
      .filter((s): s is TLShape => !!s);
    const lock = list.some((s) => !s.isLocked);
    this.updateShapes(
      list.map((s) => ({ id: s.id, type: s.type, isLocked: lock })),
    );
    if (lock) this.selectNone();
    return this;
  }

  alignShapes(
    shapes: Shapes,
    operation:
      | "bottom"
      | "center-horizontal"
      | "center-vertical"
      | "center"
      | "left"
      | "right"
      | "top",
  ) {
    const list = ids(shapes);
    const boxes = new Map(
      list.map((id) => [id, this.getShapePageBounds(id)!] as const),
    );
    const all = Box.Common([...boxes.values()]);
    for (const [id, b] of boxes) {
      const target = new Vec(b.x, b.y);
      if (operation === "left") target.x = all.minX;
      if (operation === "right") target.x = all.maxX - b.w;
      if (operation === "top") target.y = all.minY;
      if (operation === "bottom") target.y = all.maxY - b.h;
      if (operation === "center-horizontal" || operation === "center")
        target.x = all.midX - b.w / 2;
      if (operation === "center-vertical" || operation === "center")
        target.y = all.midY - b.h / 2;
      this.nudgeShapes([id], Vec.Sub(target, b));
    }
    return this;
  }
  distributeShapes(shapes: Shapes, operation: "horizontal" | "vertical") {
    const list = ids(shapes)
      .map((id) => ({ id, b: this.getShapePageBounds(id)! }))
      .filter((e) => e.b);
    if (list.length < 3) return this;
    const horizontal = operation === "horizontal";
    list.sort((a, b) =>
      horizontal ? a.b.midX - b.b.midX : a.b.midY - b.b.midY,
    );
    const first = list[0].b,
      last = list[list.length - 1].b;
    const total = list.reduce(
      (sum, e) => sum + (horizontal ? e.b.w : e.b.h),
      0,
    );
    const span = horizontal ? last.maxX - first.minX : last.maxY - first.minY;
    const gap = (span - total) / (list.length - 1);
    let cursor = horizontal ? first.minX : first.minY;
    for (const { id, b } of list) {
      const offset = horizontal
        ? new Vec(cursor - b.minX, 0)
        : new Vec(0, cursor - b.minY);
      this.nudgeShapes([id], offset);
      cursor += (horizontal ? b.w : b.h) + gap;
    }
    return this;
  }

  groupShapes(shapes: TLShape[] | TLShapeId[]) {
    const list = ids(shapes);
    if (list.length < 2 || !this.hasShapeUtil("group")) return this;
    const parents = new Set(list.map((id) => this.getShape(id)?.parentId));
    const parentId = (
      parents.size === 1 ? [...parents][0] : this.getCurrentPageId()
    ) as TLParentId;
    const groupId = createShapeId();
    const index = list
      .map((id) => this.getShape(id)!.index)
      .sort()
      .at(-1)!;
    this.store.put([
      this.complete({ id: groupId, type: "group", parentId, index, props: {} }),
    ]);
    this.reparentShapes(list, groupId);
    this.select(groupId);
    return this;
  }
  ungroupShapes(groupIds: TLShapeId[]) {
    const released: TLShapeId[] = [];
    for (const id of groupIds) {
      const group = this.getShape(id);
      if (group?.type !== "group") continue;
      const children = this.getSortedChildIdsForParent(id);
      this.reparentShapes(children, group.parentId, group.index);
      released.push(...children);
      this.store.remove([id]);
    }
    if (released.length) this.select(...released);
    return this;
  }

  getHighestIndexForParent(parent: TLPage | TLParentId | TLShape): IndexKey {
    const children = this.getSortedChildIdsForParent(parent);
    const last = children.length
      ? this.getShape(children[children.length - 1])?.index
      : undefined;
    return getIndexAbove(last);
  }
  getShapeAndDescendantIds(shapeIds: TLShapeId[]) {
    const out = new Set<TLShapeId>();
    const walk = (id: TLShapeId) => {
      if (out.has(id)) return;
      out.add(id);
      for (const child of this.getSortedChildIdsForParent(id)) walk(child);
    };
    for (const id of shapeIds) walk(id);
    return out;
  }
  getAncestorPageId(shape?: TLShape | TLShapeId): TLPageId | undefined {
    let current = shape ? this.getShape(idOf(shape)) : undefined;
    while (current) {
      if (!isShapeId(current.parentId)) return current.parentId as TLPageId;
      current = this.getShape(current.parentId);
    }
    return undefined;
  }
  hasAncestor(shape: TLShape | TLShapeId | undefined, ancestorId: TLShapeId) {
    let current = shape ? this.getShape(idOf(shape)) : undefined;
    while (current && isShapeId(current.parentId)) {
      if (current.parentId === ancestorId) return true;
      current = this.getShape(current.parentId);
    }
    return false;
  }
  findShapeAncestor(
    shape: TLShape | TLShapeId,
    predicate: (parent: TLShape) => boolean,
  ) {
    let current = this.getShape(idOf(shape));
    while (current && isShapeId(current.parentId)) {
      const parent = this.getShape(current.parentId);
      if (!parent) return undefined;
      if (predicate(parent)) return parent;
      current = parent;
    }
    return undefined;
  }
  /** Clicking inside a group selects the group, unless already focused inside it. */
  getOutermostSelectableShape(
    shape: TLShape | TLShapeId,
    filter?: (shape: TLShape) => boolean,
  ) {
    let current = this.getShape(idOf(shape))!;
    let match = current;
    const focused = this.pageState().focusedGroupId;
    while (current && isShapeId(current.parentId)) {
      const parent = this.getShape(current.parentId);
      if (!parent || parent.id === focused) break;
      if (parent.type === "group" && (!filter || filter(parent)))
        match = parent;
      current = parent;
    }
    return match;
  }
  isShapeHidden(shape: TLShape | TLShapeId): boolean {
    const record = this.getShape(idOf(shape));
    if (!record) return false;
    const visibility = this.opts.getShapeVisibility?.(record, this);
    if (visibility === "hidden") return true;
    if (visibility === "visible") return false;
    return isShapeId(record.parentId)
      ? this.isShapeHidden(record.parentId)
      : false;
  }
  isShapeOrAncestorLocked(shape?: TLShape | TLShapeId): boolean {
    let current = shape ? this.getShape(idOf(shape)) : undefined;
    while (current) {
      if (current.isLocked) return true;
      current = isShapeId(current.parentId)
        ? this.getShape(current.parentId)
        : undefined;
    }
    return false;
  }
  isShapeFrameLike(shape: TLShape | TLShapeId) {
    const record = this.getShape(idOf(shape));
    if (!record) return false;
    const util = this.getUtil(record);
    return (
      this.opts.isFrameLike?.(util) ??
      !!(util.constructor as { isFrameLike?: boolean }).isFrameLike
    );
  }

  // ----- geometry and coordinates -----

  getShapeGeometry(shape: TLShape | TLShapeId): Geometry2d {
    const record = this.getShape(idOf(shape));
    if (!record) throw new Error("Missing shape");
    return this.geometryCache.get(record.id)!;
  }
  getShapePageTransform(shape: TLShape | TLShapeId): Mat {
    return this.transformCache.get(idOf(shape) as TLShapeId) ?? Mat.Identity();
  }
  getShapePageBounds(shape: TLShape | TLShapeId): Box | undefined {
    const record = this.getShape(idOf(shape));
    if (!record) return undefined;
    return this.getShapePageTransform(record).applyToBounds(
      this.getShapeGeometry(record).bounds,
    );
  }
  /** Points of the shape's outline in page space. */
  getShapePageOutline(shape: TLShape | TLShapeId) {
    return this.getShapePageTransform(shape).applyToPoints(
      this.getShapeGeometry(shape).vertices,
    );
  }
  getPointInShapeSpace(shape: TLShape | TLShapeId, point: VecLike) {
    return this.getShapePageTransform(shape).invert().applyToPoint(point);
  }
  getPointInParentSpace(shape: TLShape | TLShapeId, point: VecLike) {
    const record = this.getShape(idOf(shape));
    if (!record || !isShapeId(record.parentId)) return Vec.From(point);
    return this.getPointInShapeSpace(record.parentId, point);
  }

  getShapeAtPoint(point: VecLike, opts: ShapeAtPointOptions = {}) {
    const margin =
      typeof opts.margin === "number" ? opts.margin : (opts.margin?.[0] ?? 0);
    const shapes = this.getCurrentPageShapesSorted();
    for (let i = shapes.length - 1; i >= 0; i--) {
      const shape = shapes[i];
      if (this.isShapeHidden(shape)) continue;
      if (opts.filter && !opts.filter(shape)) continue;
      if (!opts.hitLocked && this.isShapeOrAncestorLocked(shape)) continue;
      const geometry = this.getShapeGeometry(shape);
      const local = this.getPointInShapeSpace(shape, point);
      if (this.isClippedAway(shape, point)) continue;
      if (opts.hitLabels && hitsLabel(geometry, local, margin)) return shape;
      if (this.isShapeFrameLike(shape)) {
        // Frames are hit on their edges and title; inside only when asked.
        if (geometry.hitTestPoint(local, margin, false)) return shape;
        if (opts.hitFrameInside && geometry.bounds.containsPoint(local, margin))
          return shape;
        continue;
      }
      if (geometry.hitTestPoint(local, margin, opts.hitInside ?? false))
        return shape;
    }
    return undefined;
  }

  /**
   * The page-space polygon a shape's ancestors (sheets, tracing paper) clip it
   * to, or undefined when nothing clips it. Nested clips are approximated by the
   * intersection of their bounds.
   */
  getShapeClipPolygon(id: TLShapeId): Vec[] | undefined {
    const shape = this.getShape(id);
    if (!shape) return undefined;
    let polygon: Vec[] | undefined;
    let child = shape;
    let parent = this.getShape(shape.parentId);
    while (parent) {
      const util = this.getUtil(parent);
      if (util.shouldClipChild?.(child)) {
        const clip = util.getClipPath?.(parent) as VecLike[] | undefined;
        if (clip?.length) {
          const page = this.getShapePageTransform(parent)
            .applyToPoints(clip)
            .map((p) => Vec.From(p));
          if (!polygon) polygon = page;
          else {
            const a = Box.FromPoints(polygon),
              b = Box.FromPoints(page);
            const x = Math.max(a.minX, b.minX),
              y = Math.max(a.minY, b.minY);
            polygon = new Box(
              x,
              y,
              Math.max(0, Math.min(a.maxX, b.maxX) - x),
              Math.max(0, Math.min(a.maxY, b.maxY) - y),
            ).corners.map((p) => Vec.From(p));
          }
        }
      }
      child = parent;
      parent = this.getShape(parent.parentId);
    }
    return polygon;
  }

  /** Whether a point is outside the clip area an ancestor imposes on this shape. */
  isClippedAway(shape: TLShape, point: VecLike) {
    let current = shape;
    while (isShapeId(current.parentId)) {
      const parent = this.getShape(current.parentId);
      if (!parent) break;
      const util = this.getUtil(parent);
      if (util.shouldClipChild?.(current)) {
        const clip = util.getClipPath?.(parent) as VecLike[] | undefined;
        if (clip) {
          const local = this.getPointInShapeSpace(parent, point);
          const box = Box.FromPoints(clip);
          if (!box.containsPoint(local)) return true;
        }
      }
      current = parent;
    }
    return false;
  }

  pageToScreen(point: VecLike) {
    const { x, y, z } = this.getCamera();
    const s = this.screenBounds.get();
    return new Vec(
      (point.x + x) * z + s.x,
      (point.y + y) * z + s.y,
      point.z ?? 1,
    );
  }
  screenToPage(point: VecLike) {
    const { x, y, z } = this.getCamera();
    const s = this.screenBounds.get();
    return new Vec(
      (point.x - s.x) / z - x,
      (point.y - s.y) / z - y,
      point.z ?? 1,
    );
  }
  /** Container-relative screen point to page point. */
  viewportToPage(point: VecLike) {
    const { x, y, z } = this.getCamera();
    return new Vec(point.x / z - x, point.y / z - y);
  }

  // ----- selection, editing, tools -----

  select(...shapes: TLShape[] | TLShapeId[]) {
    return this.setSelectedShapes(shapes);
  }
  selectNone() {
    if (this.getSelectedShapeIds().length) this.setSelectedShapes([]);
    return this;
  }
  setSelectedShapes(shapes: Shapes) {
    const next = ids(shapes).filter((id) => this.getShape(id));
    this.updatePageState({ selectedShapeIds: next });
    return this;
  }
  getSelectedShapeIds(): TLShapeId[] {
    return this.pageState().selectedShapeIds;
  }
  getSelectedShapes() {
    return this.getSelectedShapeIds()
      .map((id) => this.getShape(id))
      .filter((s): s is TLShape => !!s);
  }
  getOnlySelectedShape() {
    const selected = this.getSelectedShapes();
    return selected.length === 1 ? selected[0] : null;
  }
  getOnlySelectedShapeId() {
    return this.getOnlySelectedShape()?.id ?? null;
  }
  getSelectionPageBounds() {
    const boxes = this.getSelectedShapeIds()
      .map((id) => this.getShapePageBounds(id))
      .filter((b): b is Box => !!b);
    return boxes.length ? Box.Common(boxes) : null;
  }
  getHoveredShapeId() {
    return this.pageState().hoveredShapeId;
  }
  setHoveredShape(id: TLShapeId | null) {
    if (this.pageState().hoveredShapeId !== id)
      this.updatePageState({ hoveredShapeId: id });
  }
  setHintingShapes(shapes: Shapes) {
    this.updatePageState({ hintingShapeIds: ids(shapes) });
    return this;
  }
  getHintingShapeIds() {
    return this.pageState().hintingShapeIds;
  }
  /** Alignment guides shown while shapes are being moved. */
  getSnapLines(): SnapLine[] {
    return this.snapLines.get();
  }
  setSnapLines(lines: SnapLine[]) {
    if (lines.length || this.snapLines.get().length) this.snapLines.set(lines);
  }
  getErasingShapeIds() {
    return this.pageState().erasingShapeIds;
  }
  setErasingShapes(ids: TLShapeId[]) {
    this.updatePageState({ erasingShapeIds: ids });
    return this;
  }
  getEditingShape() {
    const id = this.getEditingShapeId();
    return id ? this.getShape(id) : undefined;
  }
  getEditingShapeId() {
    return this.pageState().editingShapeId;
  }
  setEditingShape(shape: null | TLShape | TLShapeId) {
    const id = shape ? idOf(shape) : null;
    if (id && !this.getUtil(this.getShape(id)!).canEdit?.(this.getShape(id)))
      return this;
    if (id !== this.getEditingShapeId()) {
      this.history.commit();
      if (id) this.history.mark("Edit text", { hold: true });
    }
    this.updatePageState({ editingShapeId: id });
    return this;
  }
  getCroppingShapeId() {
    return this.pageState().croppingShapeId;
  }
  setCroppingShape(shape: null | TLShape | TLShapeId) {
    this.updatePageState({ croppingShapeId: shape ? idOf(shape) : null });
    return this;
  }
  getRichTextEditor() {
    return this.richText.get();
  }
  setRichTextEditor(editor: RichTextEditor | null) {
    this.richText.set(editor);
    this.history.touch();
  }
  setCurrentTool(id: string, info: object = {}) {
    if (!this.root.children?.[id.split(".")[0]]) {
      this.opts.onUnknownTool?.(id);
      return this;
    }
    this.root.transition(id, info);
    this.syncToolPath();
    if (this.isIn("select.idle")) this.history.commit();
    return this;
  }

  // ----- external content -----

  private readonly external = new Map<
    string,
    ((info: any) => void | Promise<void>) | null
  >();
  registerExternalContentHandler(
    type: string,
    handler: ((info: any) => void | Promise<void>) | null,
  ) {
    this.external.set(type, handler);
    return this;
  }
  private readonly externalAssets = new Map<
    string,
    ((info: any) => Promise<TLAsset> | TLAsset) | null
  >();
  /** Builds the asset for external content (the "url" handler makes link cards). */
  registerExternalAssetHandler(
    type: string,
    handler: ((info: any) => Promise<TLAsset> | TLAsset) | null,
  ) {
    this.externalAssets.set(type, handler);
    return this;
  }
  async putExternalContent(info: ExternalContent) {
    // A handler registered as null turns that kind of content off.
    if (this.external.has(info.type)) {
      const handler = this.external.get(info.type);
      return handler ? handler(info) : undefined;
    }
    if (info.type === "content")
      this.putContentOntoCurrentPage(info.content, { point: info.point });
    if (info.type === "url") return this.putUrl(info.url, info.point);
  }
  /** Paste clipboard data at a page point: files, Planroom content, a link or text. */
  async paste(data: { files: File[]; text: string }, point: VecLike) {
    if (this.getIsReadonly()) return;
    const { files, text } = data;
    this.markHistoryStoppingPoint("paste");
    if (files.length) return this.putExternalContent({ type: "files", files, point });
    if (text.startsWith(CLIPBOARD_TAG))
      return this.putExternalContent({
        type: "content",
        content: JSON.parse(text.slice(CLIPBOARD_TAG.length)),
        point,
      });
    if (/^https?:\/\/\S+$/.test(text.trim()))
      return this.putExternalContent({ type: "url", url: text.trim(), point });
    if (text) return this.putExternalContent({ type: "text", text, point });
  }
  /** Read the system clipboard and paste it at a page point (the canvas menu's Paste). */
  async pasteFromClipboard(point: VecLike) {
    const files: File[] = [];
    let text = "";
    for (const item of await navigator.clipboard.read())
      for (const type of item.types) {
        const blob = await item.getType(type);
        if (type === "text/plain") text ||= await blob.text();
        else if (!type.startsWith("text/"))
          files.push(new File([blob], `Pasted.${type.split("/")[1] ?? "bin"}`, { type }));
      }
    return this.paste({ files, text }, point);
  }
  /** A pasted link becomes an embed when the site allows it, otherwise a link card. */
  private async putUrl(url: string, point?: VecLike) {
    if (this.getIsReadonly()) return;
    const at = point ?? this.getViewportPageBounds().center;
    const embedUtil = this.utils.get("embed") as
      | { getEmbedDefinition?(url: string): { definition: { width: number; height: number } } | undefined }
      | undefined;
    const embed = embedUtil?.getEmbedDefinition?.(url);
    this.markHistoryStoppingPoint("paste link");
    const id = createShapeId();
    if (embed) {
      const { width: w, height: h } = embed.definition;
      this.createShape({ id, type: "embed", x: at.x - w / 2, y: at.y - h / 2, props: { w, h, url } } as TLShapePartial);
    } else {
      if (!this.utils.has("bookmark")) return;
      const makeAsset = this.externalAssets.get("url");
      const asset = makeAsset ? await makeAsset({ type: "url", url }) : undefined;
      if (asset) this.createAssets([asset]);
      this.createShape({
        id,
        type: "bookmark",
        x: at.x - 150,
        y: at.y - 160,
        props: { url, assetId: asset?.id ?? null },
      } as TLShapePartial);
    }
    if (this.getShape(id)) this.select(id);
  }
  getShortcut(combo: string) {
    return this.opts.shortcuts?.[combo];
  }
  getCurrentTool() {
    const current = this.root.getCurrent()!;
    return current as StateNode & { getPath(): string };
  }
  getCurrentToolId() {
    this.toolPath.get();
    return this.root.getCurrent()?.id ?? "";
  }
  isIn(path: string) {
    const current = this.toolPath.get();
    return current === path || current.startsWith(`${path}.`);
  }
  isInAny(...paths: string[]) {
    return paths.some((p) => this.isIn(p));
  }
  cancel() {
    this.dispatch({ type: "misc", name: "cancel" });
    return this;
  }
  setCursor(cursor: Partial<TLCursor>) {
    const current = this.getInstanceState().cursor;
    if (
      cursor.type !== undefined &&
      cursor.type === current.type &&
      (cursor.rotation ?? current.rotation) === current.rotation
    )
      return this;
    return this.updateInstanceState({ cursor: { ...current, ...cursor } });
  }

  // ----- styles -----

  getStyleForNextShape<T>(style: StyleProp<T>): T {
    const value = (
      this.getInstanceState().stylesForNextShape as Record<string, unknown>
    )[style.id];
    return (value === undefined ? style.defaultValue : value) as T;
  }
  setStyleForNextShapes<T>(
    style: StyleProp<T>,
    value: T,
    historyOptions?: HistoryOptions,
  ) {
    const styles = {
      ...this.getInstanceState().stylesForNextShape,
      [style.id]: value,
    };
    return this.updateInstanceState(
      { stylesForNextShape: styles },
      historyOptions,
    );
  }
  setStyleForSelectedShapes<S extends StyleProp<any>>(
    style: S,
    value: S extends StyleProp<infer V> ? V : never,
  ) {
    const updates: TLShapePartial[] = [];
    for (const shape of this.getSelectedShapes()) {
      const key = this.styleProps(shape.type).find(
        ([, s]) => s.id === style.id,
      )?.[0];
      if (key)
        updates.push({
          id: shape.id,
          type: shape.type,
          props: { [key]: value },
          ...(key === "color" ? {meta:{...shape.meta,exactColor:null,exactStrokeColor:null,exactFillColor:null}} : {}),
        });
    }
    return this.updateShapes(updates);
  }
  setOpacityForNextShapes(opacity: number, historyOptions?: HistoryOptions) {
    return this.updateInstanceState(
      { opacityForNextShape: opacity },
      historyOptions,
    );
  }

  // ----- camera and viewport -----

  getCamera(): TLCamera {
    return (
      (this.store.get(
        CameraRecordType.createId(this.getCurrentPageId()),
      ) as TLCamera) ?? ({ x: 0, y: 0, z: 1 } as TLCamera)
    );
  }
  setCamera(point: VecLike, opts?: CameraMoveOptions) {
    if (this.cameraOptions.get().isLocked && !opts?.force) return this;
    const current = this.getCamera();
    const target = {
      x: point.x,
      y: point.y,
      z: clampZoom(point.z ?? current.z),
    };
    if (opts?.animation && !opts.immediate)
      return this.animateCamera(target, opts.animation);
    this.stopCameraAnimation();
    this.putCamera(target);
    return this;
  }
  private putCamera(target: { x: number; y: number; z: number }) {
    const id = CameraRecordType.createId(this.getCurrentPageId());
    this.ensurePageState(this.getCurrentPageId());
    this.history.run(
      () => this.store.update(id, (c) => ({ ...c, ...target })),
      "ignore",
    );
  }
  private animateCamera(
    target: { x: number; y: number; z: number },
    animation: { easing?(t: number): number; duration?: number },
  ) {
    this.stopCameraAnimation();
    const start = { ...this.getCamera() };
    const duration = animation.duration ?? 320;
    const ease = animation.easing ?? ((t: number) => 1 - (1 - t) ** 3);
    const began = performance.now();
    const step = () => {
      const t = Math.min(1, (performance.now() - began) / duration);
      const k = ease(t);
      this.putCamera({
        x: start.x + (target.x - start.x) * k,
        y: start.y + (target.y - start.y) * k,
        z: start.z + (target.z - start.z) * k,
      });
      if (t < 1) this.animation = requestAnimationFrame(step);
    };
    this.animation = requestAnimationFrame(step);
    return this;
  }
  getCameraOptions() {
    return this.cameraOptions.get();
  }
  setCameraOptions(opts: Partial<CameraOptions>) {
    this.cameraOptions.set({ ...this.cameraOptions.get(), ...opts });
    return this;
  }
  stopCameraAnimation() {
    if (this.animation) cancelAnimationFrame(this.animation);
    this.animation = 0;
    return this;
  }
  getZoomLevel() {
    return this.getCamera().z;
  }
  /** Zoom keeping a screen point fixed (defaults to the viewport centre). */
  zoomAround(z: number, screenPoint?: VecLike, opts?: CameraMoveOptions) {
    const s = this.screenBounds.get();
    const p = screenPoint
      ? new Vec(screenPoint.x - s.x, screenPoint.y - s.y)
      : new Vec(s.w / 2, s.h / 2);
    const { x, y, z: oldZ } = this.getCamera();
    const next = clampZoom(z);
    return this.setCamera(
      {
        x: x + p.x / next - p.x / oldZ,
        y: y + p.y / next - p.y / oldZ,
        z: next,
      },
      opts,
    );
  }
  /** Back to 100%, keeping the point (or the viewport centre) still. */
  resetZoom(point?: VecLike, opts?: CameraMoveOptions) {
    return this.zoomAround(1, point ? this.pageToScreen(point) : undefined, opts);
  }
  zoomIn(point?: VecLike, opts?: CameraMoveOptions) {
    const z = this.getZoomLevel();
    const steps = this.cameraOptions.get().zoomSteps;
    const next = steps.find((s) => s > z + 1e-6) ?? steps[steps.length - 1];
    return this.zoomAround(
      next,
      point ? this.pageToScreen(point) : undefined,
      opts,
    );
  }
  zoomOut(point?: VecLike, opts?: CameraMoveOptions) {
    const z = this.getZoomLevel();
    const steps = this.cameraOptions.get().zoomSteps;
    const next = [...steps].reverse().find((s) => s < z - 1e-6) ?? steps[0];
    return this.zoomAround(
      next,
      point ? this.pageToScreen(point) : undefined,
      opts,
    );
  }
  zoomToBounds(
    bounds: BoxLike,
    opts: { inset?: number; targetZoom?: number } & CameraMoveOptions = {},
  ) {
    const s = this.screenBounds.get();
    const inset = opts.inset ?? Math.min(256, s.w * 0.28);
    const z = clampZoom(
      opts.targetZoom ??
        Math.min(
          (s.w - inset) / Math.max(1, bounds.w),
          (s.h - inset) / Math.max(1, bounds.h),
        ),
    );
    return this.setCamera(
      {
        x: -bounds.x + (s.w / z - bounds.w) / 2,
        y: -bounds.y + (s.h / z - bounds.h) / 2,
        z,
      },
      opts,
    );
  }
  zoomToSelection(opts?: CameraMoveOptions) {
    const bounds = this.getSelectionPageBounds();
    if (bounds)
      this.zoomToBounds(bounds, {
        ...opts,
        targetZoom: Math.max(1, this.getZoomLevel()),
      });
    return this;
  }
  zoomToFit(opts?: CameraMoveOptions) {
    const bounds = this.getCurrentPageBounds();
    if (bounds) this.zoomToBounds(bounds, opts);
    return this;
  }
  centerOnPoint(point: VecLike, opts?: CameraMoveOptions) {
    const s = this.screenBounds.get();
    const z = this.getZoomLevel();
    return this.setCamera(
      { x: -point.x + s.w / z / 2, y: -point.y + s.h / z / 2, z },
      opts,
    );
  }
  getViewportScreenBounds() {
    return this.screenBounds.get();
  }
  getViewportPageBounds() {
    const s = this.screenBounds.get();
    const { x, y, z } = this.getCamera();
    return new Box(-x, -y, s.w / z, s.h / z);
  }
  updateViewportScreenBounds(
    screenBounds: BoxModel | HTMLElement,
    _center?: boolean,
  ) {
    const isElement =
      typeof HTMLElement !== "undefined" && screenBounds instanceof HTMLElement;
    const box = screenBounds as BoxModel;
    const rect = isElement
      ? (screenBounds as HTMLElement).getBoundingClientRect()
      : { left: box.x, top: box.y, width: box.w, height: box.h };
    const next = new Box(
      rect.left,
      rect.top,
      Math.max(1, rect.width),
      Math.max(1, rect.height),
    );
    const current = this.screenBounds.__unsafe__getWithoutCapture();
    if (
      current.x !== next.x ||
      current.y !== next.y ||
      current.w !== next.w ||
      current.h !== next.h
    )
      this.screenBounds.set(next);
    return this;
  }

  // ----- assets, content, bindings, export -----

  getAsset<T extends TLAsset>(asset: T | T["id"]) {
    return this.store.get(typeof asset === "string" ? asset : asset.id) as
      T | undefined;
  }
  getAssets() {
    return this.store.query.records("asset").get();
  }
  createAssets(assets: TLAsset[]) {
    if (assets.length) this.store.put(assets);
    return this;
  }
  updateAssets(assets: TLAssetPartial[]) {
    const updates = assets
      .map((partial) => {
        const asset = this.getAsset(partial.id);
        return asset
          ? ({
              ...asset,
              ...partial,
              props: { ...asset.props, ...(partial.props ?? {}) },
              meta: { ...asset.meta, ...(partial.meta ?? {}) },
            } as TLAsset)
          : null;
      })
      .filter((a): a is TLAsset => !!a);
    if (updates.length) this.store.put(updates);
    return this;
  }
  async uploadAsset(asset: TLAsset, file: File, abortSignal?: AbortSignal) {
    const assets = (
      this.store.props as {
        assets: {
          upload(
            a: TLAsset,
            f: File,
            s?: AbortSignal,
          ): Promise<{ src: string; meta?: never }>;
        };
      }
    ).assets;
    return assets.upload(asset, file, abortSignal);
  }
  createBindings(partials: TLBindingCreate[]) {
    const types = this.store.schema.types as unknown as Record<
      string,
      { create(r: object): TLRecord }
    >;
    this.store.put(
      partials.map((b) =>
        types.binding.create({
          ...b,
          id: b.id ?? createBindingId(),
          meta: b.meta ?? {},
        }),
      ),
    );
    return this;
  }

  getContentFromCurrentPage(shapes: Shapes): CanvasContent | undefined {
    const rootIds = ids(shapes).filter((id) => this.getShape(id));
    if (!rootIds.length) return undefined;
    const all = [...this.getShapeAndDescendantIds(rootIds)];
    const records = all.map((id) => this.getShape(id)!);
    const set = new Set(all);
    const assetIds = new Set(
      records
        .map((s) => (s.props as { assetId?: string }).assetId)
        .filter((a): a is string => !!a),
    );
    const bindings = (
      this.store.query.records("binding").get() as TLBinding[]
    ).filter((b) => set.has(b.fromId) && set.has(b.toId));
    // Roots carry their page position so content can be placed anywhere.
    const roots = rootIds.map((id) => {
      const shape = this.getShape(id)!;
      const t = this.getShapePageTransform(shape);
      const p = t.point();
      return {
        ...shape,
        x: p.x,
        y: p.y,
        rotation: t.rotation(),
        parentId: this.getCurrentPageId(),
      };
    });
    return {
      shapes: [...roots, ...records.filter((s) => !rootIds.includes(s.id))],
      rootShapeIds: rootIds,
      bindings,
      assets: [...assetIds]
        .map((id) => this.getAsset(id as TLAsset["id"]))
        .filter((a): a is TLAsset => !!a),
      schema: this.store.schema.serialize(),
    };
  }

  putContentOntoCurrentPage(
    content: CanvasContent,
    opts: {
      point?: VecLike;
      select?: boolean;
      preservePosition?: boolean;
      duplicateOffset?: VecLike;
    } = {},
  ) {
    if (this.getIsReadonly()) return this;
    const idMap = new Map<string, TLShapeId>();
    for (const shape of content.shapes) idMap.set(shape.id, createShapeId());
    const pageId = this.getCurrentPageId();
    const roots = new Set(content.rootShapeIds as string[]);
    const rootBox = Box.Common(
      content.shapes
        .filter((s) => roots.has(s.id))
        .map((s) => ({ x: s.x, y: s.y, w: 1, h: 1 })),
    );
    let offset = new Vec(
      opts.duplicateOffset?.x ?? 0,
      opts.duplicateOffset?.y ?? 0,
    );
    if (!opts.preservePosition && opts.point)
      offset = Vec.Sub(opts.point, rootBox.point);
    let index = this.getHighestIndexForParent(pageId);
    const shapes = content.shapes.map((shape) => {
      const root = roots.has(shape.id);
      const next = {
        ...shape,
        id: idMap.get(shape.id)!,
        parentId: root ? pageId : (idMap.get(shape.parentId) ?? pageId),
        ...(root
          ? { x: shape.x + offset.x, y: shape.y + offset.y, index }
          : {}),
      } as TLShape;
      if (root) index = getIndexAbove(index);
      return next;
    });
    const assets = content.assets.filter((a) => !this.getAsset(a.id));
    this.run(() => {
      if (assets.length) this.store.put(assets);
      this.store.put(shapes);
      if (content.bindings?.length)
        this.store.put(
          content.bindings.map((b) => ({
            ...b,
            id: createBindingId(),
            fromId: idMap.get(b.fromId)!,
            toId: idMap.get(b.toId)!,
          })),
        );
    });
    if (opts.select !== false)
      this.select(
        ...shapes.filter((s) => s.parentId === pageId).map((s) => s.id),
      );
    return this;
  }

  /** The shapes (and their contents) as a standalone SVG document. */
  async getSvgString(shapes: Shapes, opts: ImageExportOptions = {}) {
    return exportSvg(this, ids(shapes), { ...opts, fonts: this.opts.fonts });
  }
  async toImage(
    shapes: Shapes,
    opts: ImageExportOptions = {},
  ): Promise<{ blob: Blob; width: number; height: number }> {
    const svg = await this.getSvgString(shapes, opts);
    if (!svg) throw new Error("There is nothing to export.");
    if (opts.format === "svg")
      return {
        blob: new Blob([svg.svg], { type: "image/svg+xml" }),
        width: svg.width,
        height: svg.height,
      };
    return rasterize(svg.svg, svg.width, svg.height, {
      ...opts,
      format: opts.format ?? "png",
    });
  }

  /** Run with shape locks ignored (programmatic edits such as lock toggles). */
  withoutLocks(fn: () => void) {
    this.ignoreLock = true;
    try {
      fn();
    } finally {
      this.ignoreLock = false;
    }
  }

  getResizeTarget(): ResizeHandle | null {
    return null;
  }
}

const clampZoom = (z: number) => Math.min(8, Math.max(0.05, z));

/** Labels (such as a sheet's title) inside a group geometry. */
function hitsLabel(
  geometry: Geometry2d,
  point: VecLike,
  margin: number,
): boolean {
  const children = (geometry as unknown as { children?: Geometry2d[] })
    .children;
  if (geometry.isLabel) return geometry.bounds.containsPoint(point, margin);
  return !!children?.some((child) => hitsLabel(child, point, margin));
}

/** Measure HTML text off-screen with the given text styles. */
function measureHtml(
  html: string,
  opts: Parameters<CanvasTextMeasure["measureHtml"]>[1],
) {
  const el = document.createElement("div");
  // Measured as drawn: without the page's own paragraph margins and line height.
  el.className = "canvas-text-measure";
  Object.assign(el.style, {
    position: "absolute",
    visibility: "hidden",
    left: "-99999px",
    top: "0",
    whiteSpace: "pre-wrap",
    overflowWrap: opts.disableOverflowWrapBreaking ? "normal" : "break-word",
    fontStyle: opts.fontStyle,
    fontWeight: opts.fontWeight,
    fontFamily: opts.fontFamily,
    fontSize: `${opts.fontSize}px`,
    lineHeight: String(opts.lineHeight),
    padding: opts.padding,
    width: opts.maxWidth === null ? "max-content" : "auto",
    maxWidth: opts.maxWidth === null ? "none" : `${opts.maxWidth}px`,
    minWidth: opts.minWidth ? `${opts.minWidth}px` : "0",
    ...(opts.otherStyles ?? {}),
  });
  el.innerHTML = html;
  document.body.append(el);
  const rect = el.getBoundingClientRect();
  const scrollWidth = el.scrollWidth;
  el.remove();
  return { x: 0, y: 0, w: rect.width, h: rect.height, scrollWidth };
}
